import Phaser from 'phaser';
import { CHEST_TIER_VIEW } from '@snake-royalle/shared';
import { sfx } from '../audio/SFX.js';
import { isCalmMode } from '../audio/CalmMode.js';

/**
 * Top-center HUD widget that shows the next chest the player is working
 * toward and a progress bar from 0 to that tier's cost (per-tier, not
 * cumulative — feels like you're starting fresh on each new chest).
 *
 * Below the bar: 5 mini chest icons (one per tier). Each lights up when
 * that tier is unlocked. A small `X/5` counter on the right tracks total.
 *
 * When a threshold is crossed mid-match the widget pulses, the new mini
 * chest pops in, and `chest_appear` plays. Per-match progress only — no
 * carryover for v1, so demo & paid behave the same.
 */

const TIER_SPRITE_KEY: Record<string, string> = {
  SALVAGE:   'chest_salvage_closed',
  FIELD:     'chest_field_closed',
  CASE:      'chest_case_closed',
  VAULT:     'chest_vault_closed',
  LEVIATHAN: 'chest_leviathan_closed',
};

const TIER_COLOR: Record<string, number> = {
  SALVAGE:   0xC8A055,
  FIELD:     0xC0C0C0,
  CASE:      0xFFD166,
  VAULT:     0xFF8844,
  LEVIATHAN: 0xCC55FF,
};

export class ChestProgressWidget {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private bg: Phaser.GameObjects.Graphics;
  private chestSprite: Phaser.GameObjects.Image;
  private nameText: Phaser.GameObjects.Text;
  private progressText: Phaser.GameObjects.Text;
  private barTrack: Phaser.GameObjects.Graphics;
  private barFill: Phaser.GameObjects.Graphics;
  private unlockedCountText: Phaser.GameObjects.Text;
  private maxedText: Phaser.GameObjects.Text;
  private currentTierIndex: number = 0;
  private barW: number = 180;
  private barH: number = 7;
  private widgetW: number = 320;
  private widgetH: number = 64;
  private chestSize: number = 48;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    const cx = scene.scale.width / 2;
    const cy = 38; // top-center, compact widget
    this.container = scene.add.container(cx, cy).setDepth(110).setScrollFactor(0);

    this.bg = scene.add.graphics();
    this.drawPanel(this.bg, false);
    this.container.add(this.bg);

    // === LEFT: big chest icon ===
    this.chestSprite = scene.add.image(-this.widgetW / 2 + 32, 0, 'chest_salvage_closed')
      .setOrigin(0.5)
      .setDisplaySize(this.chestSize, this.chestSize);
    this.container.add(this.chestSprite);

    // === MIDDLE: name (top), bar (bottom), progress text top-right of middle ===
    const midLeft = -this.widgetW / 2 + 64;
    const midRight = midLeft + this.barW + 8;

    this.nameText = scene.add.text(midLeft, -14, 'SALVAGE CRATE', {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: '#c8d0e0',
      fontStyle: 'bold',
    }).setOrigin(0, 0.5);
    this.nameText.setLetterSpacing?.(1);
    this.container.add(this.nameText);

    this.progressText = scene.add.text(midRight, -14, '0 / 15', {
      fontFamily: 'monospace',
      fontSize: '11px',
      color: '#888899',
    }).setOrigin(1, 0.5);
    this.container.add(this.progressText);

    const barY = 4;
    this.barTrack = scene.add.graphics();
    this.barFill = scene.add.graphics();
    this.drawBar(this.barTrack, this.barFill, midLeft, barY, 0, TIER_COLOR.SALVAGE);
    this.container.add(this.barTrack);
    this.container.add(this.barFill);

    // === RIGHT: just the X/5 counter ===
    this.unlockedCountText = scene.add.text(this.widgetW / 2 - 14, 0, '0/5', {
      fontFamily: 'monospace',
      fontSize: '13px',
      color: '#666677',
      fontStyle: 'bold',
    }).setOrigin(1, 0.5);
    this.container.add(this.unlockedCountText);

    // Maxed message (hidden — replaces middle column when all unlocked)
    this.maxedText = scene.add.text((midLeft + midRight) / 2, 0, 'ALL CHESTS UNLOCKED', {
      fontFamily: 'monospace',
      fontSize: '13px',
      color: '#ffd166',
      fontStyle: 'bold',
    }).setOrigin(0.5).setVisible(false);
    this.container.add(this.maxedText);

    scene.scale.on('resize', this.onResize, this);
  }

  /** Update progress. `total` = coinsBanked + coinsCarried (this match). */
  update(total: number): void {
    const nextIdx = CHEST_TIER_VIEW.findIndex(t => total < t.cumulativeThreshold);

    // Tier changed (advanced) — pulse + sound
    const advanced = nextIdx === -1
      ? this.currentTierIndex < CHEST_TIER_VIEW.length
      : nextIdx > this.currentTierIndex;
    if (advanced) {
      this.pulse();
      sfx(this.scene, 'chest_appear');
      this.currentTierIndex = nextIdx === -1 ? CHEST_TIER_VIEW.length : nextIdx;
    }

    // Update unlocked counter regardless
    const unlockedCount = nextIdx === -1 ? CHEST_TIER_VIEW.length : nextIdx;
    this.unlockedCountText.setText(`${unlockedCount}/${CHEST_TIER_VIEW.length}`);
    this.unlockedCountText.setColor(unlockedCount > 0 ? '#c8d0e0' : '#666677');

    if (nextIdx === -1) {
      // All unlocked — hide tier progress UI, show celebration
      this.chestSprite.setVisible(false);
      this.nameText.setVisible(false);
      this.progressText.setVisible(false);
      this.barTrack.setVisible(false);
      this.barFill.setVisible(false);
      this.maxedText.setVisible(true);
      return;
    }

    const tier = CHEST_TIER_VIEW[nextIdx];
    const prevThreshold = nextIdx > 0 ? CHEST_TIER_VIEW[nextIdx - 1].cumulativeThreshold : 0;
    const tierProgress = total - prevThreshold;       // resets to 0 each new tier
    const tierCost = tier.cost;                       // bar maxes at this tier's cost
    const ratio = Phaser.Math.Clamp(tierProgress / tierCost, 0, 1);

    this.chestSprite.setTexture(TIER_SPRITE_KEY[tier.tier]);
    this.nameText.setText(tier.name.toUpperCase());
    this.progressText.setText(`${tierProgress} / ${tierCost}`);

    const barX = -this.widgetW / 2 + 64;
    const barY = 4;
    this.drawBar(this.barTrack, this.barFill, barX, barY, ratio, TIER_COLOR[tier.tier]);
  }

  /** Pulse the whole widget — single quick scale-up + border flash */
  private pulse(): void {
    if (isCalmMode()) return; // no-stimulus: skip the scale-up + border flash
    this.scene.tweens.add({
      targets: this.container,
      scaleX: 1.08,
      scaleY: 1.08,
      duration: 140,
      yoyo: true,
      ease: 'Quad.easeOut',
    });
    this.drawPanel(this.bg, true);
    this.scene.time.delayedCall(240, () => this.drawPanel(this.bg, false));
  }

  private drawPanel(g: Phaser.GameObjects.Graphics, glow: boolean): void {
    g.clear();
    const w = this.widgetW;
    const h = this.widgetH;
    const r = 10;
    g.fillStyle(0x0a0e18, 0.92);
    g.fillRoundedRect(-w / 2, -h / 2, w, h, r);
    const borderColor = glow ? 0x6ef0a0 : 0x4a6a8c;
    const borderAlpha = glow ? 1 : 0.7;
    g.lineStyle(glow ? 2 : 1, borderColor, borderAlpha);
    g.strokeRoundedRect(-w / 2, -h / 2, w, h, r);
  }

  private drawBar(
    track: Phaser.GameObjects.Graphics,
    fill: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    ratio: number,
    color: number,
  ): void {
    track.clear();
    track.fillStyle(0x1a2030, 1);
    track.fillRoundedRect(x, y, this.barW, this.barH, 2);

    fill.clear();
    if (ratio > 0) {
      fill.fillStyle(color, 1);
      fill.fillRoundedRect(x, y, this.barW * ratio, this.barH, 2);
      fill.fillStyle(0xffffff, 0.18);
      fill.fillRect(x, y, this.barW * ratio, 1);
    }
  }

  private onResize(gameSize: Phaser.Structs.Size): void {
    this.container.setX(gameSize.width / 2);
  }

  destroy(): void {
    this.scene.scale.off('resize', this.onResize, this);
    this.container.destroy();
  }
}
